import type { UserNotificationDto } from "@mvk/shared";
import { toast } from "sonner";
import { isApiEnabled } from "../api/config";
import { visitorAuthStorage } from "../api/authStorage";
import { notificationsApi } from "../api/notifications";
import { shouldEnableBrowserPwa } from "./channelHosts";
import { debugLog } from "./debugLog";

const POLL_MS = 45_000;
const seenIds = new Set<number>();

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

async function ensureNotificationPermission(): Promise<boolean> {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

function showBrowserNotification(n: UserNotificationDto) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const notification = new Notification(n.title, {
      body: n.body,
      tag: `mvk-${n.id}`,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // ignore
  }
}

function presentNotification(n: UserNotificationDto) {
  toast(n.title, { description: n.body, duration: 8000 });
  showBrowserNotification(n);
}

async function subscribeWebPush(): Promise<void> {
  if (!shouldEnableBrowserPwa()) return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

  try {
    const { publicKey, enabled } = await notificationsApi.getVapidPublicKey();
    if (!enabled || !publicKey) {
      debugLog.info("push", "VAPID not configured — in-app poll only");
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
    }

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    await notificationsApi.registerPush({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      platform: "web",
      userAgent: navigator.userAgent,
    });
    debugLog.info("push", "web push subscribed");
  } catch (err) {
    debugLog.warn("push", "subscribe failed", err);
  }
}

export const notificationService = {
  async pollUnread(): Promise<void> {
    if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated()) return;

    try {
      const { notifications } = await notificationsApi.list(true);
      for (const n of notifications) {
        if (seenIds.has(n.id)) continue;
        seenIds.add(n.id);
        presentNotification(n);
        void notificationsApi.markRead(n.id).catch(() => undefined);
      }
    } catch {
      // silent — next poll
    }
  },

  async requestPermission(): Promise<void> {
    const granted = await ensureNotificationPermission();
    if (granted) await subscribeWebPush();
  },

  startPolling(): () => void {
    void this.requestPermission();
    void this.pollUnread();

    const onFocus = () => {
      void this.pollUnread();
    };
    window.addEventListener("focus", onFocus);

    const timer = window.setInterval(() => {
      void this.pollUnread();
    }, POLL_MS);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
  },
};
